"use client"

import * as React from "react"
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react"
import { cn } from '../../../lib/utils'
import { Button } from '../button'
import { Checkbox } from '../checkbox'
import { GroupSection, getAccessorKey } from "./group-section"
import type { GroupedDataViewProps, DataGroup, GroupedViewSortingState, SortDirection } from "./types"

/**
 * Sort items within a group
 */
function sortItems<TData>(
  items: TData[],
  sorting: GroupedViewSortingState | null
): TData[] {
  if (!sorting || !sorting.direction) {
    return items
  }

  const { columnId, direction } = sorting

  return [...items].sort((a, b) => {
    const aValue = (a as Record<string, unknown>)[columnId]
    const bValue = (b as Record<string, unknown>)[columnId]

    // Handle null/undefined
    if (aValue == null && bValue == null) return 0
    if (aValue == null) return direction === "asc" ? 1 : -1
    if (bValue == null) return direction === "asc" ? -1 : 1

    // String comparison
    if (typeof aValue === "string" && typeof bValue === "string") {
      return direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    }

    // Number comparison
    if (typeof aValue === "number" && typeof bValue === "number") {
      return direction === "asc" ? aValue - bValue : bValue - aValue
    }

    // Date comparison
    if (aValue instanceof Date && bValue instanceof Date) {
      return direction === "asc"
        ? aValue.getTime() - bValue.getTime()
        : bValue.getTime() - aValue.getTime()
    }

    // Fallback to string comparison
    const aStr = String(aValue)
    const bStr = String(bValue)
    return direction === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
  })
}

/**
 * Render header content for a column
 */
function renderHeaderContent(
  column: { id?: string; header?: unknown; accessorKey?: string; meta?: { headerClassName?: string } },
  sortDirection: SortDirection,
  enableSorting: boolean,
  onSort: () => void
): React.ReactNode {
  const accessorKey = column.accessorKey

  // If header is a function (like DataTableColumnHeader), create mock column
  if (typeof column.header === "function") {
    const mockColumn = {
      getIsSorted: () => sortDirection,
      toggleSorting: () => onSort(),
      getCanSort: () => enableSorting,
      getCanHide: () => false,
      toggleVisibility: () => {},
    }

    try {
      return column.header({ column: mockColumn } as never)
    } catch {
      return column.id || accessorKey || ""
    }
  }

  // Simple string header with sorting
  if (typeof column.header === "string") {
    if (!enableSorting) {
      return column.header
    }

    return (
      <Button
        variant="ghost"
        size="sm"
        className="-ml-3 h-8 data-[state=open]:bg-accent"
        onClick={onSort}
      >
        {column.header}
        {sortDirection === "asc" ? (
          <ArrowUp className="ml-2 h-4 w-4" />
        ) : sortDirection === "desc" ? (
          <ArrowDown className="ml-2 h-4 w-4" />
        ) : (
          <ArrowUpDown className="ml-2 h-4 w-4" />
        )}
      </Button>
    )
  }

  return column.id || accessorKey || ""
}

/**
 * GroupedDataView component - displays data in collapsible groups
 *
 * Features:
 * - Grouping by any field with collapsible sections
 * - Sticky columns (first N columns fixed during horizontal scroll)
 * - Row selection with checkboxes (select all per group + individual rows)
 * - Sorting within groups by clicking column headers
 * - Uses same column definitions as DataTable
 */
export function GroupedDataView<TData>({
  columns,
  data,
  groupBy,
  enableSelection = false,
  enableSorting = true,
  stickyColumns = 0,
  selection: controlledSelection,
  onSelectionChange,
  getRowId: customGetRowId,
  onRowClick,
  onRowDoubleClick,
  formatGroupLabel,
  defaultExpanded = true,
  emptyState,
  className,
  maxHeight,
}: GroupedDataViewProps<TData>) {
  // Default getRowId function
  const getRowId = React.useCallback(
    (row: TData, index: number): string => {
      if (customGetRowId) {
        return customGetRowId(row, index)
      }
      // Try to use 'id' field, otherwise fall back to index
      const maybeId = (row as Record<string, unknown>).id
      return maybeId != null ? String(maybeId) : String(index)
    },
    [customGetRowId]
  )

  // Internal selection state (used when uncontrolled)
  const [internalSelection, setInternalSelection] = React.useState<Set<string>>(
    new Set()
  )

  // Use controlled or uncontrolled selection
  const selection = controlledSelection ?? internalSelection

  // Internal sorting state
  const [sorting, setSorting] = React.useState<GroupedViewSortingState | null>(null)

  // Track expanded state for each group
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(
    new Set()
  )

  // Initialize expanded groups on mount or when data changes
  React.useEffect(() => {
    if (defaultExpanded) {
      const groupValues = new Set(
        data.map((item) => String((item as Record<string, unknown>)[groupBy] ?? ""))
      )
      setExpandedGroups(groupValues)
    }
  }, [data, groupBy, defaultExpanded])

  // Group data by the specified field
  const groups = React.useMemo((): DataGroup<TData>[] => {
    const groupMap = new Map<string, TData[]>()

    data.forEach((item) => {
      const groupValue = String(
        (item as Record<string, unknown>)[groupBy] ?? ""
      )
      const existing = groupMap.get(groupValue) || []
      existing.push(item)
      groupMap.set(groupValue, existing)
    })

    // Convert to array and sort items within each group
    return Array.from(groupMap.entries()).map(([groupValue, items]) => ({
      groupValue,
      groupLabel: groupValue || "(No Value)",
      items: sortItems(items, sorting),
      isExpanded: expandedGroups.has(groupValue),
    }))
  }, [data, groupBy, sorting, expandedGroups])

  // Handle selection change
  const handleSelectionChange = React.useCallback(
    (rowIds: string[], selected: boolean) => {
      const newSelection = new Set(selection)

      if (selected) {
        rowIds.forEach((id) => newSelection.add(id))
      } else {
        rowIds.forEach((id) => newSelection.delete(id))
      }

      if (onSelectionChange) {
        onSelectionChange(newSelection)
      } else {
        setInternalSelection(newSelection)
      }
    },
    [selection, onSelectionChange]
  )

  // Handle sort change
  const handleSortChange = React.useCallback((columnId: string) => {
    setSorting((current) => {
      if (!current || current.columnId !== columnId) {
        return { columnId, direction: "asc" }
      }
      if (current.direction === "asc") {
        return { columnId, direction: "desc" }
      }
      // Clear sorting
      return null
    })
  }, [])

  // Handle group expand/collapse toggle
  const handleToggleExpand = React.useCallback((groupValue: string) => {
    setExpandedGroups((current) => {
      const newSet = new Set(current)
      if (newSet.has(groupValue)) {
        newSet.delete(groupValue)
      } else {
        newSet.add(groupValue)
      }
      return newSet
    })
  }, [])

  // Calculate row index offset for each group
  const getRowIndexOffset = React.useCallback(
    (groupIndex: number): number => {
      let offset = 0
      for (let i = 0; i < groupIndex; i++) {
        offset += groups[i].items.length
      }
      return offset
    },
    [groups]
  )

  // Filter columns based on selection state
  const displayColumns = React.useMemo(() => {
    if (enableSelection) {
      return columns
    }
    return columns.filter((col) => col.id !== "select")
  }, [columns, enableSelection])

  // Get sort direction for a column
  const getSortDirection = (columnId: string): SortDirection => {
    if (!sorting || sorting.columnId !== columnId) {
      return null
    }
    return sorting.direction
  }

  // Check if all rows are selected (for header checkbox)
  const allRowIds = React.useMemo(
    () => data.map((row, idx) => getRowId(row, idx)),
    [data, getRowId]
  )
  const allSelected = allRowIds.length > 0 && allRowIds.every((id) => selection.has(id))
  const someSelected = allRowIds.some((id) => selection.has(id))

  const handleSelectAll = (checked: boolean | "indeterminate") => {
    handleSelectionChange(allRowIds, checked === true)
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div
        className={cn(
          "rounded-lg bg-sunken",
          className
        )}
        style={{ maxHeight }}
      >
        <div className="flex items-center justify-center p-8 text-muted-foreground">
          {emptyState ?? "No data available"}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn("rounded-lg bg-sunken overflow-hidden w-full min-w-0 max-w-full", className)}
      style={{ maxHeight }}
    >
      <div className={cn("overflow-auto min-w-0 overscroll-contain", maxHeight && "h-full")}>
        <table className="w-full caption-bottom text-sm border-collapse table-auto">
          {/* Single Header */}
          <thead className="sticky top-0 z-40 bg-panel">
            <tr>
              {displayColumns.map((column, colIndex) => {
                const accessorKey = getAccessorKey(column)
                const columnId = column.id || accessorKey || `col-${colIndex}`
                const isSelectionColumn = column.id === "select"
                const sortDirection = getSortDirection(columnId)
                const isSticky = colIndex < stickyColumns

                // Calculate left offset for sticky columns
                // First column: 0, subsequent sticky columns accumulate
                const stickyLeft = isSticky
                  ? colIndex === 0
                    ? 0
                    : colIndex === 1
                      ? 40 // Width of checkbox column
                      : undefined
                  : undefined

                return (
                  <th
                    key={columnId}
                    className={cn(
                      "text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-0.5",
                      isSticky && "sticky bg-panel z-50",
                      column.meta?.headerClassName
                    )}
                    style={stickyLeft !== undefined ? { left: stickyLeft } : undefined}
                  >
                    {isSelectionColumn ? (
                      <Checkbox
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all"
                      />
                    ) : (
                      renderHeaderContent(
                        column as { id?: string; header?: unknown; accessorKey?: string; meta?: { headerClassName?: string } },
                        sortDirection,
                        enableSorting,
                        () => handleSortChange(columnId)
                      )
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          {/* Groups */}
          <tbody>
            {groups.map((group, groupIndex) => (
              <GroupSection
                key={group.groupValue}
                group={group}
                columns={displayColumns}
                stickyColumns={stickyColumns}
                enableSelection={enableSelection}
                selection={selection}
                onSelectionChange={handleSelectionChange}
                getRowId={getRowId}
                onRowClick={onRowClick}
                onRowDoubleClick={onRowDoubleClick}
                onToggleExpand={() => handleToggleExpand(group.groupValue)}
                formatGroupLabel={formatGroupLabel}
                rowIndexOffset={getRowIndexOffset(groupIndex)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
